import React, { useState, useEffect } from 'react'
import { TextField, InputAdornment, IconButton, Tooltip, Box, Typography } from '@mui/material'
import { CheckCircle as ValidIcon, Error as ErrorIcon, Help as HelpIcon } from '@mui/icons-material'

interface CNPInputProps {
  value: string
  onChange: (value: string, extractedData?: { dob: string; sex: string }) => void
  disabled?: boolean
  error?: string
  label?: string
  required?: boolean
}

interface CNPValidation {
  isValid: boolean
  isPseudo: boolean
  pseudoType?: 'type1' | 'type2'
  dob?: string
  sex?: string
  error?: string
}

/**
 * Validate Romanian CNP format and extract DOB/sex
 */
const validateCNP = (cnp: string): CNPValidation => {
  if (!cnp || cnp.length !== 13) {
    return { isValid: false, isPseudo: false, error: 'CNP trebuie să aibă 13 cifre' }
  }

  if (!/^\d+$/.test(cnp)) {
    return { isValid: false, isPseudo: false, error: 'CNP trebuie să conțină doar cifre' }
  }

  const firstDigit = parseInt(cnp[0])

  // Check if pseudo-CNP
  if (firstDigit === 9) {
    // Pseudo-CNP Type 1
    return validatePseudoCNPType1(cnp)
  } else if (firstDigit === 8) {
    // Pseudo-CNP Type 2
    return { isValid: true, isPseudo: true, pseudoType: 'type2' }
  }

  // Real CNP validation
  return validateRealCNP(cnp)
}

const validateRealCNP = (cnp: string): CNPValidation => {
  const sexDigit = parseInt(cnp[0])
  const year = parseInt(cnp.substring(1, 3))
  const month = parseInt(cnp.substring(3, 5))
  const day = parseInt(cnp.substring(5, 7))

  // Validate sex digit (1-6 Romanian citizens, 7-9 residents)
  if (sexDigit === 0) {
    return { isValid: false, isPseudo: false, error: 'CNP invalid: cifra de sex incorectă' }
  }

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, isPseudo: false, error: 'CNP invalid: luna incorectă' }
  }

  // Validate day
  if (day < 1 || day > 31) {
    return { isValid: false, isPseudo: false, error: 'CNP invalid: ziua incorectă' }
  }

  // Determine century and full year
  let fullYear: number
  if (sexDigit === 1 || sexDigit === 2) {
    fullYear = 1900 + year
  } else if (sexDigit === 3 || sexDigit === 4) {
    fullYear = 1800 + year
  } else if (sexDigit === 5 || sexDigit === 6) {
    fullYear = 2000 + year
  } else if (sexDigit === 7 || sexDigit === 8) {
    fullYear = year > 24 ? 1900 + year : 2000 + year
  } else {
    fullYear = 2000 + year
  }

  // Validate control digit
  const controlKey = '279146358279'
  let controlSum = 0
  for (let i = 0; i < 12; i++) {
    controlSum += parseInt(cnp[i]) * parseInt(controlKey[i])
  }
  let controlDigit = controlSum % 11
  if (controlDigit === 10) controlDigit = 1

  if (parseInt(cnp[12]) !== controlDigit) {
    return { isValid: false, isPseudo: false, error: 'CNP invalid: cifră de control incorectă' }
  }

  // Extract sex (odd = male, even = female)
  const sex = sexDigit % 2 === 1 ? 'M' : 'F'

  // Format DOB as YYYY-MM-DD
  const dob = `${fullYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

  return { isValid: true, isPseudo: false, dob, sex }
}

const validatePseudoCNPType1 = (cnp: string): CNPValidation => {
  // Format: 9AALLZZNNNNNN
  const year = parseInt(cnp.substring(1, 3))
  const month = parseInt(cnp.substring(3, 5))
  const day = parseInt(cnp.substring(5, 7))

  // Validate month
  if (month < 1 || month > 12) {
    return { isValid: false, isPseudo: true, pseudoType: 'type1', error: 'Pseudo-CNP invalid: luna incorectă' }
  }

  // Validate day
  if (day < 1 || day > 31) {
    return { isValid: false, isPseudo: true, pseudoType: 'type1', error: 'Pseudo-CNP invalid: ziua incorectă' }
  }

  // Determine full year (simple logic: < 50 = 2000+, >= 50 = 1900+)
  const fullYear = year < 50 ? 2000 + year : 1900 + year

  const dob = `${fullYear}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`

  // Note: Pseudo-CNP Type 1 doesn't encode sex, so we can't extract it
  return { isValid: true, isPseudo: true, pseudoType: 'type1', dob }
}

const CNPInput: React.FC<CNPInputProps> = ({
  value,
  onChange,
  disabled = false,
  error,
  label = 'CNP',
  required = false,
}) => {
  const [validation, setValidation] = useState<CNPValidation>({ isValid: false, isPseudo: false })
  const [showHelp, setShowHelp] = useState(false)

  useEffect(() => {
    if (value && value.length === 13) {
      const result = validateCNP(value)
      setValidation(result)

      // If valid, notify parent with extracted data
      if (result.isValid && result.dob) {
        onChange(value, { dob: result.dob, sex: result.sex || '' })
      }
    } else {
      setValidation({ isValid: false, isPseudo: false })
    }
  }, [value])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value.replace(/\D/g, '').slice(0, 13)
    onChange(newValue)
  }

  const getStatusIcon = () => {
    if (!value || value.length < 13) return null

    if (validation.isValid) {
      return (
        <Tooltip title={validation.isPseudo ? 'Pseudo-CNP valid' : 'CNP valid'}>
          <ValidIcon color="success" />
        </Tooltip>
      )
    }

    return (
      <Tooltip title={validation.error || 'CNP invalid'}>
        <ErrorIcon color="error" />
      </Tooltip>
    )
  }

  return (
    <Box>
      <TextField
        fullWidth
        label={label}
        value={value}
        onChange={handleChange}
        disabled={disabled}
        required={required}
        error={!!error || (value.length === 13 && !validation.isValid)}
        helperText={
          error ||
          (value.length === 13 && !validation.isValid ? validation.error : '') ||
          (validation.isValid && validation.isPseudo ? `Pseudo-CNP Type ${validation.pseudoType === 'type1' ? '1' : '2'}` : '')
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {getStatusIcon()}
              <Tooltip title="Informații CNP">
                <IconButton size="small" onClick={() => setShowHelp(!showHelp)}>
                  <HelpIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        inputProps={{
          maxLength: 13,
          pattern: '[0-9]*',
          inputMode: 'numeric',
        }}
      />
      {showHelp && (
        <Box sx={{ mt: 1, p: 1, bgcolor: 'info.light', borderRadius: 1 }}>
          <Typography variant="caption" display="block" gutterBottom>
            <strong>CNP Real:</strong> 13 cifre, extrage automat data nașterii și sexul
          </Typography>
          <Typography variant="caption" display="block" gutterBottom>
            <strong>Pseudo-CNP Type 1:</strong> Început cu 9, pentru pacienți fără CNP român
          </Typography>
          <Typography variant="caption" display="block">
            <strong>Pseudo-CNP Type 2:</strong> Început cu 8, generat automat de sistem
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CNPInput

